import {
  store as createStore,
  autoEffect
} from '@risingstack/react-easy-state'
import { debug } from '../utilities/logger'

/**
 * Special store to manage S.E.O metadatas.
 * Used in header & to actually update the DOM meta in head
 */

/**
 * [store]
 */
const DEFAULT_STORE = {
  logo: 'Trouver un jardinier à domicile avec cmonjardinier.com',
  alt: 'Trouver un jardinier à domicile avec cmonjardinier.com',
  title:
    "Cmonjardinier : Trouvez votre jardinier paysagiste pour l'entretien, l'aménagement ou la création de vos jardins et espaces verts.",
  description:
    'Cmonjardinier vous aide à trouver un jardinier. Réduction fiscale de 50 %. Demandez un devis gratuit de jardinage !',
  keywords: 'devis jardinier, devis jardinage, jardinier, paysagiste'
}

const store = createStore({
  values: Object.assign({}, DEFAULT_STORE),
  lastUpdate: null,
  errors: [],
  setValues,
  clear,
  validate
})

/**
 * [balises]
 */
const BALISES = {}
BALISES.title = document.getElementsByTagName('title')[0]
BALISES.description = createMeta('description')
BALISES.keywords = createMeta('keywords')

function createMeta (name) {
  const meta = document.createElement('meta')
  meta.setAttribute('name', name)
  meta.setAttribute('content', '')

  var firstMeta = document.getElementsByTagName('meta')[0]
  document.getElementsByTagName('head')[0].insertBefore(meta, firstMeta)
  return meta
}

/**
 * Simplified validation for compatibility
 */
function validateMetadataBasic (title, description, city, postalCode) {
  const errors = []

  if (!title || title.includes('undefined')) {
    errors.push('Title contains undefined or is empty')
  }
  if (!description || description.includes('undefined')) {
    errors.push('Description contains undefined or is empty')
  }

  return {
    valid: errors.length === 0,
    errors: errors
  }
}

/**
 * Secure validation and updating of metadata
 */
function setValues (newValues, context) {
  context = context || {}

  // Validation BEFORE fallbacks (critical errors only)
  const validation = validateMetadataBasic(
    newValues.title || store.values.title,
    newValues.description || store.values.description,
    context.city,
    context.postalCode
  )

  // Critical error log (corrupted data)
  if (validation.errors.length > 0) {
    console.error(
      `[Metadata] Validation errors for ${context.url || 'unknown page'}:`,
      validation.errors
    )

    // FALLBACKS ONLY for missing data
    const fallbackValues = generateFallbacksForMissingData(newValues, context)
    store.values = Object.assign({}, store.values, fallbackValues)
    store.errors = validation.errors
  } else {
    // Valid metadata, normal update
    store.values = Object.assign({}, store.values, newValues)
    store.errors = []
  }

  store.lastUpdate = Date.now()
  debug(
    `[stores][metadata.setValues] Updated metadata for ${
      context.city || 'unknown'
    }`
  )
}

/**
 *  Fallbacks ONLY for missing data (no optimization)
 */
function generateFallbacksForMissingData (originalValues, context) {
  const fallbacks = {} // ⚠️ Seulement ce qui manque

  // Fallback title ONLY if undefined/empty
  if (!originalValues.title || originalValues.title.includes('undefined')) {
    if (context.city && context.postalCode) {
      fallbacks.title = `Jardiniers paysagistes à ${context.city} (${context.postalCode}) - Cmonjardinier`
    } else if (context.city) {
      fallbacks.title = `Jardiniers paysagistes à ${context.city} - Cmonjardinier`
    } else {
      fallbacks.title = DEFAULT_STORE.title
    }
  }

  // Fallback description ONLY if undefined/empty
  if (
    !originalValues.description ||
    originalValues.description.includes('undefined')
  ) {
    if (context.city && context.postalCode) {
      fallbacks.description = `Trouvez les meilleurs jardiniers paysagistes à ${context.city} (${context.postalCode}). Devis gratuit et réduction fiscale de 50%.`
    } else if (context.city) {
      fallbacks.description = `Trouvez les meilleurs jardiniers paysagistes à ${context.city}. Devis gratuit et réduction fiscale de 50%.`
    } else {
      fallbacks.description = DEFAULT_STORE.description
    }
  }

  // Fallback keywords ONLY if undefined/empty
  if (
    !originalValues.keywords ||
    originalValues.keywords.includes('undefined')
  ) {
    const baseKeywords = DEFAULT_STORE.keywords
    if (context.city && context.postalCode) {
      fallbacks.keywords = `${context.city}, ${context.postalCode}, ${baseKeywords}`
    } else if (context.city) {
      fallbacks.keywords = `${context.city}, ${baseKeywords}`
    } else {
      fallbacks.keywords = baseKeywords
    }
  }

  return fallbacks
}

/**
 * Validation of current metadata
 */
function validate (context) {
  context = context || {}
  return validateMetadataBasic(
    store.values.title,
    store.values.description,
    context.city,
    context.postalCode
  )
}

function update () {
  debug(`[stores][metadata.update] Updating metadatas`)

  // Safety check before injection
  const safeTitle =
    store.values.title && !store.values.title.includes('undefined')
      ? store.values.title
      : DEFAULT_STORE.title

  const safeDescription =
    store.values.description && !store.values.description.includes('undefined')
      ? store.values.description
      : DEFAULT_STORE.description

  const safeKeywords =
    store.values.keywords && !store.values.keywords.includes('undefined')
      ? store.values.keywords
      : DEFAULT_STORE.keywords

  // Safe injection
  try {
    BALISES.title.innerText = safeTitle
    BALISES.description.setAttribute('content', safeDescription)
    BALISES.keywords.setAttribute('content', safeKeywords)

    debug(
      `[stores][metadata.update] Successfully updated: title="${safeTitle.substring(
        0,
        50
      )}..."`
    )
  } catch (error) {
    console.error(
      '[stores][metadata.update] Failed to update metadata:',
      error
    )
  }
}

function clear () {
  store.values = Object.assign({}, DEFAULT_STORE)
  store.errors = []
  store.lastUpdate = null
  debug('[stores][metadata.clear] Metadata cleared to defaults')
}

autoEffect(update)

export default store



// WEBPACK FOOTER //
// ./src/stores/metadata.js